import { ReactNode, createContext, useContext, useState } from "react"
import { BoardOrientation } from "react-chessboard/dist/chessboard/types"
import { Game } from "@/types/serializers"
import { Result, useStockfish } from "./useStockfish"
import { isEqual } from "lodash-es"
import getGameTitle from "@/utils/getGameTitle"
import { useGameMoves } from "./useGameMoves"
import { usePage } from "@inertiajs/react"
import { GameShowProps } from "@/types/types"

type GameContext = {
  initialGame: Game
  orientation: BoardOrientation
  toggleOrientation: () => void
  title: string
  id: string
  isDraft: boolean
  analyze: boolean
  setAnalyze: (analyze: boolean) => void
  analysis: Result
  isAnalyzing: boolean
  isEdited: boolean
}

const GameContext = createContext<GameContext>({} as GameContext)

export function GameProvider({
  children,
  game: gameProp,
}: {
  children: ReactNode
  game?: Game
}) {
  const { game: pageGame } = usePage<GameShowProps>().props
  const initialGame = gameProp ?? pageGame

  const { game, movesHistory } = useGameMoves()
  const { id, draft } = initialGame

  const [orientation, setOrientation] = useState<BoardOrientation>("white")
  const [analyze, setAnalyze] = useState(false)
  const { results: analysis, isAnalyzing } = useStockfish(
    analyze ? game.fen() : undefined,
    initialGame.chess960
  )
  const isEdited = !isEqual(initialGame.moves, movesHistory)
  const gameTitle = getGameTitle(initialGame)

  const toggleOrientation = () =>
    setOrientation((o) => (o === "white" ? "black" : "white"))

  return (
    <GameContext.Provider
      value={{
        initialGame,
        orientation,
        toggleOrientation,
        title: gameTitle,
        id,
        isDraft: draft,
        analyze,
        setAnalyze,
        analysis,
        isAnalyzing,
        isEdited,
      }}
    >
      {children}
    </GameContext.Provider>
  )
}

export function useGame() {
  const context = useContext(GameContext)
  if (context === undefined) {
    throw new Error("useGame must be used within a GameProvider")
  }
  return context
}
